import React from 'react';
import { Modal, Button, Form, Row, Col, Alert, Table } from 'react-bootstrap';
import {
  fmtNum,
  mergeLineaPatch,
  recalcLinea,
  recalcNota,
  tipoNfLabel,
  bloquesLineasConTotalesPorGrado,
  ordenarLineasPorGrado,
} from './inventarioCalculos';
import { CABECERA_FIELDS, GESTION_FIELDS, LINEA_FIELDS } from './inventarioFieldConfig';
import {
  applyGestionNota,
  ESTADO_CERRADO,
  ESTADO_DISPONIBLE,
  ESTADO_EN_PROCESO,
  esEstadoAutomatico,
  opcionesTipoMovimiento,
  resolverEstadoMercaderia,
} from './inventarioEstados';
import { applyGradoToLinea, opcionesAlmacen } from './inventarioOcUtils';
import DateInput from '../../components/DateInput/DateInput';

function formatDateDisplay(v) {
  if (v == null || v === '') return '—';
  const s = String(v).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s || '—';
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function CabeceraField({ spec, nota, patch, almOpts, incoterms }) {
  const { field, type, label } = spec;
  const required = field === 'peso_liquido' || field === 'peso_bruto';
  const shownLabel = label;

  switch (type) {
    case 'tipo_nf':
      return (
        <Form.Group>
          <Form.Label>{shownLabel}</Form.Label>
          <Form.Select value={nota.tipo_nf ?? ''} onChange={(e) => patch({ tipo_nf: e.target.value })}>
            <option value="0">Entrada (0)</option>
            <option value="1">Salida (1)</option>
          </Form.Select>
          <Form.Text className="text-muted">{tipoNfLabel(nota.tipo_nf)}</Form.Text>
        </Form.Group>
      );
    case 'readonly':
      return (
        <Form.Group>
          <Form.Label>{shownLabel}</Form.Label>
          <Form.Control
            readOnly
            value={field === 'proveedor' ? nota.nombre_proveedor ?? '' : nota[field] ?? ''}
          />
        </Form.Group>
      );
    case 'almacen':
      return (
        <Form.Group>
          <Form.Label>{shownLabel}</Form.Label>
          {almOpts.opciones.length > 0 ? (
            <Form.Select value={nota.almacen ?? ''} onChange={(e) => patch({ almacen: e.target.value })}>
              <option value="">—</option>
              {almOpts.opciones.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </Form.Select>
          ) : (
            <Form.Control
              value={nota.almacen ?? ''}
              onChange={(e) => patch({ almacen: e.target.value })}
              disabled={almOpts.tipo === 'exw'}
            />
          )}
          {almOpts.hint ? <Form.Text className="text-muted">{almOpts.hint}</Form.Text> : null}
        </Form.Group>
      );
    case 'incoterm':
      return (
        <Form.Group>
          <Form.Label>{shownLabel}</Form.Label>
          <Form.Select
            value={nota.incoterm_id ?? ''}
            onChange={(e) => {
              const id = e.target.value;
              const inc = incoterms.find((i) => String(i.id_incoterm) === String(id));
              patch({ incoterm_id: id ? Number(id) : null, incoterm_label: inc?.nombre || '' });
            }}
          >
            <option value="">—</option>
            {incoterms.map((i) => (
              <option key={i.id_incoterm} value={i.id_incoterm}>
                {i.nombre}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      );
    case 'date':
      return (
        <Form.Group>
          <Form.Label>{shownLabel}</Form.Label>
          <DateInput
            value={(nota[field] || '').slice(0, 10)}
            onChange={(e) => patch({ [field]: e.target.value })}
          />
        </Form.Group>
      );
    default:
      return (
        <Form.Group>
          <Form.Label>{shownLabel}</Form.Label>
          <Form.Control
            value={nota[field] ?? ''}
            onChange={(e) => patch({ [field]: e.target.value })}
            required={required}
            isInvalid={required && (nota[field] == null || nota[field] === '')}
          />
          {required ? (
            <Form.Control.Feedback type="invalid">
              Campo obligatorio para guardar la NF.
            </Form.Control.Feedback>
          ) : null}
        </Form.Group>
      );
  }
}

function GestionField({ spec, nota, patchGestion, incoterms }) {
  const { field, type, label } = spec;
  const estado = resolverEstadoMercaderia(nota);

  if (type === 'tipo_movimiento') {
    const opts = opcionesTipoMovimiento(nota, incoterms);
    return (
      <Form.Group>
        <Form.Label>{label}</Form.Label>
        <Form.Select
          value={nota.tipo_movimiento ?? ''}
          onChange={(e) => patchGestion({ tipo_movimiento: e.target.value })}
        >
          {opts.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
    );
  }

  if (type === 'fecha_movimiento') {
    return (
      <Form.Group>
        <Form.Label>{label}</Form.Label>
        <Form.Control readOnly value={formatDateDisplay(nota.fecha_mov_almacen)} />
        <Form.Text className="text-muted">Se actualiza al cambiar el tipo de movimiento.</Form.Text>
      </Form.Group>
    );
  }

  if (type === 'disponibilidad_producto') {
    return (
      <Form.Group>
        <Form.Label>{label}</Form.Label>
        {esEstadoAutomatico(nota) ? (
          <Form.Control readOnly value={estado} className="text-muted" />
        ) : (
          <Form.Select value={estado} onChange={(e) => patchGestion({ estado_mercaderia: e.target.value })}>
            <option value={ESTADO_DISPONIBLE}>{ESTADO_DISPONIBLE}</option>
            <option value={ESTADO_EN_PROCESO}>{ESTADO_EN_PROCESO}</option>
            <option value={ESTADO_CERRADO}>{ESTADO_CERRADO}</option>
          </Form.Select>
        )}
        {esEstadoAutomatico(nota) ? (
          <Form.Text className="text-muted">Estado automático según tipo movimiento o ingreso puerto.</Form.Text>
        ) : null}
      </Form.Group>
    );
  }

  if (type === 'date') {
    return (
      <Form.Group>
        <Form.Label>{label}</Form.Label>
        <DateInput
          value={(nota[field] || '').slice(0, 10)}
          onChange={(e) => patchGestion({ [field]: e.target.value })}
        />
      </Form.Group>
    );
  }

  return null;
}

function LineaCell({ spec, ln, idx, nota, updateLinea }) {
  const { field, type, decimals } = spec;

  const ch = (v) => updateLinea(idx, { [field]: v });

  if (type === 'calc') {
    return <td className="text-end">{fmtNum(ln[field], decimals)}</td>;
  }

  if (type === 'grado') {
    const opts = nota.calidades_oc || [];
    return (
      <td style={{ minWidth: spec.width, width: spec.width }}>
        <Form.Select
          size="sm"
          className="inv-nf-grado-select"
          style={{ width: '100%', minWidth: '80px' }}
          value={ln.grado ?? ''}
          onChange={(e) => updateLinea(idx, { grado: e.target.value })}
        >
          <option value="">—</option>
          {opts.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </Form.Select>
      </td>
    );
  }

  if (type === 'dim') {
    return (
      <td>
        <Form.Control size="sm" value={ln[field] ?? ''} onChange={(e) => ch(e.target.value)} />
      </td>
    );
  }

  if (type === 'decimal_editable' || type === 'decimal' || type === 'int' || type === 'number') {
    return (
      <td>
        <Form.Control size="sm" value={ln[field] ?? ''} onChange={(e) => ch(e.target.value)} />
      </td>
    );
  }

  return (
    <td>
      <Form.Control size="sm" value={ln[field] ?? ''} onChange={(e) => ch(e.target.value)} />
    </td>
  );
}

function totalGeneralDesdeBloques(bloques) {
  const acc = {
    grado: 'general',
    paquetes: 0,
    total_pcs: 0,
    total_m3: 0,
    total_brl_m3: 0,
    total_usd_m3: 0,
  };
  for (const b of bloques || []) {
    const t = b.tot;
    acc.paquetes += t.paquetes;
    acc.total_pcs += t.total_pcs;
    acc.total_m3 += t.total_m3;
    acc.total_brl_m3 += t.total_brl_m3;
    acc.total_usd_m3 += t.total_usd_m3;
  }
  return acc;
}

function FilaTotalGrado({ tot, rowClassName = 'table-light' }) {
  const etiquetaGrado =
    tot.grado === 'general' ? 'Total general' : `Total ${tot.grado}`;
  return (
    <tr className={rowClassName}>
      {LINEA_FIELDS.map((spec) => {
        if (spec.field === 'paquetes') {
          return (
            <td key={spec.field} className="text-end fw-semibold">
              {fmtNum(tot.paquetes, 0)}
            </td>
          );
        }
        if (spec.field === 'total_pcs') {
          return (
            <td key={spec.field} className="text-end fw-semibold">
              {fmtNum(tot.total_pcs, 0)}
            </td>
          );
        }
        if (spec.field === 'grado') {
          return (
            <td key={spec.field} className="fw-semibold">
              {etiquetaGrado}
            </td>
          );
        }
        if (spec.field === 'total_m3') {
          return (
            <td key={spec.field} className="text-end fw-semibold">
              {fmtNum(tot.total_m3, 3)}
            </td>
          );
        }
        if (spec.field === 'total_brl_m3') {
          return (
            <td key={spec.field} className="text-end fw-semibold">
              {fmtNum(tot.total_brl_m3, 2)}
            </td>
          );
        }
        if (spec.field === 'total_usd_m3') {
          return (
            <td key={spec.field} className="text-end fw-semibold">
              {fmtNum(tot.total_usd_m3, 2)}
            </td>
          );
        }
        return <td key={spec.field} />;
      })}
      <td />
    </tr>
  );
}

export default function InventarioNfEditModal({
  show,
  nota,
  onClose,
  onSave,
  onPersist,
  persistLabel = 'Guardar cambios',
  persisting = false,
  almacenes,
  puertos,
  incoterms,
  proveedores,
}) {
  if (!nota) return null;

  const almOpts = opcionesAlmacen({
    incotermId: nota.incoterm_id,
    incoterms,
    idProveedor: nota.id_proveedor,
    cnpj: nota.cnpj,
    proveedores,
    almacenes,
    puertos,
  });

  const patch = (p) => onSave(recalcNota({ ...nota, ...p }));
  const patchGestion = (p) => onSave(recalcNota(applyGestionNota(nota, p, { incoterms })));

  const lineas = nota.lineas || [];
  const bloquesPorGrado = bloquesLineasConTotalesPorGrado(lineas);
  const totalGeneral = totalGeneralDesdeBloques(bloquesPorGrado);

  const updateLinea = (idx, patchLn) => {
    const isGradoChange = patchLn.grado !== undefined;
    const medidas = nota._medidasPorGrado || {};
    const usd = nota._usdPorGrado || {};
    const next = lineas.map((ln, i) => {
      if (i !== idx) return ln;
      let updated = mergeLineaPatch(ln, patchLn);
      if (isGradoChange) {
        updated = applyGradoToLinea(
          updated,
          patchLn.grado,
          medidas,
          usd,
          nota._brlPorGrado || {}
        );
      }
      return recalcLinea(updated, nota.tc_bcb);
    });
    const lineasFinales = isGradoChange
      ? ordenarLineasPorGrado(next, nota.calidades_oc, idx)
      : next;
    onSave(recalcNota({ ...nota, lineas: lineasFinales }));
  };

  const addLinea = () => {
    const ref = lineas[0];

    let base;
    if (ref) {
      base = {
        paquetes: ref.paquetes ?? '',
        pcs_paq: ref.pcs_paq ?? '',
        altura_rw: ref.altura_rw ?? '',
        castigo_rw: ref.castigo_rw ?? nota.castigo_rw_default ?? '',
        grado: '',
        espesor: '',
        ancho: '',
        largo: '',
        precio_brl_unit_m3: '',
        precio_usd_unit_m3: '',
      };
    } else {
      base = {
        paquetes: '',
        pcs_paq: '',
        grado: '',
        castigo_rw: nota.castigo_rw_default ?? '',
        espesor: '',
        ancho: '',
        largo: '',
        altura_rw: '',
        precio_brl_unit_m3: '',
        precio_usd_unit_m3: '',
      };
    }

    base = recalcLinea(base, nota.tc_bcb);
    onSave(recalcNota({ ...nota, lineas: [...lineas, base] }));
  };

  const removeLinea = (idx) => {
    if (lineas.length <= 1) return;
    onSave(recalcNota({ ...nota, lineas: lineas.filter((_, i) => i !== idx) }));
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Editar NF {nota.nro_nf || '(manual)'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!nota.cnpj_match && (
          <Alert variant="warning" className="py-2 small">
            El CNPJ del XML no coincide con el proveedor seleccionado.
          </Alert>
        )}
        {nota.parse_warning && (
          <Alert variant="info" className="py-2 small">
            {nota.parse_warning}
          </Alert>
        )}

        <Row className="g-2 mb-3">
          {CABECERA_FIELDS.map((spec) => (
            <Col md={3} key={spec.field}>
              <CabeceraField
                spec={spec}
                nota={nota}
                patch={patch}
                almOpts={almOpts}
                incoterms={incoterms}
              />
            </Col>
          ))}
        </Row>

        {nota.id ? (
          <>
            <h6 className="mb-2">Disponibilidad y movimiento</h6>
            <Row className="g-2 mb-3">
              {GESTION_FIELDS.map((spec) => (
                <Col md={4} key={spec.field}>
                  <GestionField spec={spec} nota={nota} patchGestion={patchGestion} incoterms={incoterms} />
                </Col>
              ))}
            </Row>
          </>
        ) : null}

        <h6 className="mb-2">Líneas de producto</h6>
        <div className="table-responsive">
          <Table size="sm" bordered striped className="mb-0 align-middle small">
            <thead>
              <tr>
                {LINEA_FIELDS.map((f) => (
                  <th
                    key={f.field}
                    style={f.field === 'grado' ? { minWidth: f.width, width: f.width } : undefined}
                  >
                    {f.label}
                  </th>
                ))}
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {bloquesPorGrado.flatMap((bloque) => [
                ...bloque.items.map(({ ln, idx }) => (
                  <tr key={`linea-${idx}`}>
                    {LINEA_FIELDS.map((spec) => (
                      <LineaCell
                        key={spec.field}
                        spec={spec}
                        ln={ln}
                        idx={idx}
                        nota={nota}
                        updateLinea={updateLinea}
                      />
                    ))}
                    <td>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={lineas.length <= 1}
                        onClick={() => removeLinea(idx)}
                      >
                        ×
                      </Button>
                    </td>
                  </tr>
                )),
                <FilaTotalGrado key={`total-grado-${bloque.grado}`} tot={bloque.tot} />,
              ])}
              {lineas.length > 0 ? (
                <FilaTotalGrado
                  key="total-general"
                  tot={totalGeneral}
                  rowClassName="table-secondary border-top border-2"
                />
              ) : null}
            </tbody>
          </Table>
        </div>
        <Button size="sm" variant="outline-secondary" className="mt-2" onClick={addLinea}>
          + Línea
        </Button>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
        {onPersist ? (
          <Button variant="primary" disabled={persisting} onClick={() => onPersist(nota)}>
            {persisting ? 'Guardando…' : persistLabel}
          </Button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
}
